import { Title } from '@solidjs/meta'
import { type Component, createResource, createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Form from '~/components/Form'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import api from '~/services/api'

interface Theme {
  id: string
  name: string
  tokens: Record<string, string>
  isDefault: boolean
  createdAt: string
}

const BuilderThemesPage: Component = () => {
  const [themes, { refetch }] = createResource(async () => {
    try {
      const { data } = await api.get('/themes')
      return (data?.data?.items ?? []) as Theme[]
    } catch {
      return []
    }
  })

  const [showModal, setShowModal] = createSignal(false)
  const [editing, setEditing] = createSignal<Theme | null>(null)
  const [isSubmitting, setIsSubmitting] = createSignal(false)
  const alert = useAlert()
  const toast = useToast()

  const openCreate = () => {
    setEditing(null)
    setShowModal(true)
  }
  const openEdit = (t: Theme) => {
    setEditing(t)
    setShowModal(true)
  }

  const handleSubmit = async (formData: FormData) => {
    setIsSubmitting(true)
    try {
      const payload = { name: formData.get('name') as string }
      if (editing()) {
        await api.put(`/themes/${editing()!.id}`, payload)
        toast.addToast({ title: 'Tema actualizado', type: 'success' })
      } else {
        await api.post('/themes', payload)
        toast.addToast({ title: 'Tema creado', type: 'success' })
      }
      setShowModal(false)
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar tema',
      message: '¿Estás seguro de eliminar este tema?'
    })
    if (!ok) return
    try {
      await api.delete(`/themes/${id}`)
      toast.addToast({ title: 'Tema eliminado', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: err instanceof Error ? err.message : 'Error', type: 'danger' })
    }
  }

  const columns = [
    { header: 'Nombre', accessor: (t: Theme) => t.name },
    { header: 'Por defecto', accessor: (t: Theme) => (t.isDefault ? <Badge color="success">Sí</Badge> : '-') }
  ]

  return (
    <>
      <Title>Builder - Temas | Florez Jaramillo</Title>
      <Table<Theme>
        title="Temas"
        subtitle="Personaliza la apariencia de tus páginas"
        headerActions={
          <Button onClick={openCreate} prependIcon="material-symbols-light:add-circle-outline">
            Nuevo Tema
          </Button>
        }
        data={themes() ?? []}
        columns={columns}
        keyField="id"
        loading={themes.loading}
        searchable
        searchPlaceholder="Buscar tema..."
        emptyMessage="Aún no hay temas"
        actions={t => (
          <ButtonGroup size="sm" variant="soft">
            <Button color="warning" onClick={() => openEdit(t)} tooltip="Editar" icon="material-symbols-light:edit-outline" />
            <Button color="danger" onClick={() => handleDelete(t.id)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
          </ButtonGroup>
        )}
      />
      <Modal isOpen={showModal()} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">{editing() ? 'Editar Tema' : 'Nuevo Tema'}</h2>
        </Modal.Header>
        <Modal.Body>
          <Form id="theme-form" onSubmit={handleSubmit} initialValues={editing() ? { name: editing()!.name } : undefined}>
            <div class="space-y-4">
              <Input name="name" label="Nombre" required placeholder="ej: Tema Oscuro" />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="soft" color="default" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting()} form="theme-form">
              {isSubmitting() ? 'Guardando...' : editing() ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </>
  )
}

export default BuilderThemesPage
